import React from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import Section from "./PreviewSection"
import TextInput from "../components/TextInput"
import TextAreaInput from "../components/TextAreaInput"
import NumberInput from "../components/NumberInput"
import DateInput from "../components/DateInput"
import Info from "../components/Info"
import SwitchInput from "../components/SwitchInput"
import TimeInput from "../components/TimeInput"
import CheckboxInput from "../components/CheckboxInput"
import DropdownInput from "../components/DropdownInput"
import MultipleChoiceInput from "../components/MultipleChoiceInput"
import RadioInput from "../components/RadioInput"
import AddressInput from "../components/AddressInput"
import LinkInput from "../components/LinkInput"
import DividerInput from "../components/DividerInput"
import ImageInput from "../components/ImageInput"
import { Attachments } from "../components/Attachments"
import ContactInput from "../components/ContactInput"
import { formConditionsHandler, getActiveSetValue, getContrastTextColor, getHighlightAction, getWidgetsById, hasConditionalSetValue, isScoreBasedCondition, resolveDynamicSetValue, shouldApplyTodayDefault } from "../../../utils/formsHelper"
import { useMasterOptionLabel } from "../../../hooks/useMasterOptionLabel"
import UserInput from "../components/UserInput"
import TableInput from "../components/TableInput"
import RatingInput from "../components/RatingInput"
import RatingScaleInput from "../components/RatingScaleInput"
import { hideApprovalStatus, isUserApprover } from "../../../utils/requestHelper"
import { resolveFieldTranslation } from "../../../utils/formTranslations"

const availableWidth = { "12": "50%", "24": "100%" }

const Wrapper = React.memo((props) => {
    const { spaceData = {}, formType, data, currentStatus = "", ...rest } = props
    const requestDetail = rest.requestDetail
    const { userInfo, userGroups } = useSelector(state => state.user);
    const { i18n } = useTranslation();
    const { getValueLabel } = useMasterOptionLabel();
    // Render user-facing text in the active app locale, falling back to the
    // default-language values when a translation is missing. A caller can pin the
    // language with `lng` (public form links carry the language they were created
    // with). `lng` is left in `rest` so it also reaches fields nested in sections.
    const lng = rest.lng || i18n.language
    let modData = { ...resolveFieldTranslation(data, lng) }
    let setValueData = [];
    let renderElement = null;
    let conditionalChanges = [];
    let formValues = rest.formValues || {}
    if (rest.defaultRequestDetail?.value) {
        formValues = { ...rest.defaultRequestDetail.value, ...formValues }
    }
    const conditionElements = [
        ...(Array.isArray(rest.formElements) ? rest.formElements : []),
        ...(Array.isArray(rest.allFormElements) ? rest.allFormElements : []),
    ]

    if (rest?.conditions && rest?.conditions.length > 0 && rest.previewMode && formValues) {
        conditionalChanges = formConditionsHandler(rest.conditions, formValues, data, conditionElements, currentStatus, userInfo, userGroups)
        // debugger
    }
    // A "highlight" action paints its target with the colour picked on the condition,
    // and applies to any target - a section or a single field of any type. Conditions
    // that reference a Score belong to the assessment score panel instead, so the
    // colour is re-read from the non-score conditions only. This second pass runs just
    // for the widgets a highlight actually fired on; the colour lives on the raw action
    // objects, which are reduced to plain action names below.
    let highlightColor = null
    // Highlight is only for the live request form and approval details. Public
    // share links pass `isPublicForm` and a `shareToken`; both stay on `rest` so
    // nested section fields inherit the skip. Show/hide/set still run as usual.
    const skipConditionalHighlight = !!(rest.isPublicForm || rest.shareToken)
    let highlightAction = skipConditionalHighlight ? null : getHighlightAction(conditionalChanges)
    if (highlightAction) {
        const allConditions = rest.conditions || []
        const liveFormConditions = allConditions.filter(condition => !isScoreBasedCondition(condition))
        if (liveFormConditions.length < allConditions.length) {
            // A score-referencing condition was dropped, so the pass above no longer
            // stands - only when one was actually dropped is a re-evaluation needed.
            highlightAction = liveFormConditions.length > 0
                ? getHighlightAction(formConditionsHandler(liveFormConditions, formValues, data, conditionElements, currentStatus, userInfo, userGroups))
                : null
        }
        highlightColor = highlightAction ? highlightAction.value : null
    }
    if (conditionalChanges) {
        let findSet = conditionalChanges.filter(c => c?.action === "set")
        if (skipConditionalHighlight) {
            conditionalChanges = conditionalChanges.filter((change) => {
                const action = typeof change === "string" ? change : change?.action
                return action !== "highlight"
            })
        }
        if (findSet && findSet.length > 0) {
            const widgetsById = getWidgetsById(rest.formElements, rest.allFormElements)
            setValueData = findSet.map((setAction) => {
                if (setAction?.__inactiveSet) {
                    return setAction
                }
                if (setAction?.valueSource === "dynamic" && setAction?.dynamicField) {
                    if (setAction.dynamicField === "$TODAY") {
                        return { ...setAction, value: "$TODAY" }
                    }
                    return {
                        ...setAction,
                        value: resolveDynamicSetValue(
                            formValues?.[setAction.dynamicField],
                            widgetsById.get(setAction.dynamicField),
                            data.formType,
                            getValueLabel,
                            lng,
                            data
                        )
                    }
                }
                return setAction
            })
        }
        conditionalChanges = conditionalChanges.map(condition => typeof condition === "string" ? condition : condition.action)
    }
    // The value a condition writes, read before the date widget's own "today" default
    // is folded into setValueData below - only a condition keeps a hidden field alive.
    const conditionalSetValue = getActiveSetValue(setValueData, true)
    const writesConditionalValue = !!conditionalSetValue && !conditionalSetValue.__inactiveSet &&
        conditionalSetValue.value !== undefined && conditionalSetValue.value !== null && conditionalSetValue.value !== ""
    // The "today" default is written as a $TODAY set action so the widget resolves it
    // the same way a condition's dynamic date is resolved - but only while the request
    // is still being filled in. A submitted request keeps the date it was submitted
    // with; see shouldApplyTodayDefault.
    const appliesTodayDefault = rest.previewMode && data.formType === "dateForm" && data.isToday &&
        shouldApplyTodayDefault({ requestDetail, defaultRequestDetail: rest.defaultRequestDetail, fieldId: data.id, viewOnly: !!rest.disableAllFields })
    if (appliesTodayDefault) {
        const hasActiveValueSet = setValueData.some((s) => s?.value !== undefined && s?.value !== null)
        if (!hasActiveValueSet) {
            setValueData = [{ action: "set", value: "$TODAY" }, ...setValueData]
        }
    }
    // A hidden field can still be the target of a "set" action, and the value that
    // action writes belongs in the submitted values and in the step's formValues just
    // like a visible field's. The widget owns the per-type conversion of a condition
    // value (dates to dayjs, multiple choice to an array, user pickers to
    // {user, group}), so rather than writing the value here the widget stays mounted
    // and is only taken off the screen. A hidden field nothing writes into is dropped
    // exactly as before, so nothing extra is mounted for its own sake.
    const isHidden = !!(rest.previewMode && (
        (conditionalChanges && conditionalChanges.includes("hide")) ||
        (modData.hideInitially && !(conditionalChanges && conditionalChanges.includes("show")))
    ))
    let hiddenBranch = rest.hiddenBranch === true
    if (isHidden) {
        const keepForSetValue = (data.children && data.children.length > 0)
            ? hasConditionalSetValue(data, rest.conditions || [], formValues, conditionElements, currentStatus, userInfo, userGroups)
            : writesConditionalValue
        if (!keepForSetValue) {
            return <div></div>
        }
        hiddenBranch = true
        // `rest` is this render's own object, and every branch below spreads it into
        // the widget, so children of a hidden section inherit the flag from here.
        rest.hiddenBranch = true
    }
    if (conditionalChanges && conditionalChanges.includes("mandatory")) {
        modData.mandatory = true
    }
    if (conditionalChanges && conditionalChanges.includes("editable"))
        if ((rest.previewMode && (conditionalChanges && !conditionalChanges.includes("editable")) && rest.readOnly)) {
            modData.readOnly = true
        } else if (rest.previewMode && (((conditionalChanges && conditionalChanges.includes("editable")) || !rest.readOnly))) {
            modData.readOnly = false
        }
    // }
    if (rest.previewMode && rest.readOnly && (!rest.conditions || rest.conditions.length === 0)) {
        modData.readOnly = true
    }
    if (rest.freezeFields && rest.freezeFields?.[data.id] !== undefined && (!conditionalChanges || (conditionalChanges && !conditionalChanges.includes("editable")))) {
        modData.readOnly = true
    }
    const isLiveCurrentStep = !!(rest.isCurrentStepForm && !rest.disableAllFields)
    const canEditCurrentStep = isLiveCurrentStep && (
        !!userInfo?.isAdmin ||
        !!isUserApprover(userInfo, rest.defaultRequestDetail?.approvers || requestDetail?.approvers, userGroups, true)
    )
    if (!canEditCurrentStep && hideApprovalStatus.includes(requestDetail?.status) && requestDetail?.status !== "draft") {
        modData.readOnly = true
    }
    const hasRequestContext = !!(requestDetail && Object.keys(requestDetail).length > 0)
    if (hasRequestContext && !canEditCurrentStep) {
        const canEditAsAdmin = !!userInfo?.isAdmin
        const canEditAsApprover = !!isUserApprover(userInfo, rest.defaultRequestDetail?.approvers || requestDetail?.approvers, userGroups, true)
        if (!canEditAsAdmin && !canEditAsApprover) {
            if (!rest?.spaceEntitlements?.includes("Workflow_Documents_Update")) {
                modData.readOnly = true
            }
            if ((requestDetail?.status === "draft" || requestDetail.workflow?.length === 0) && requestDetail?.createdBy?.email !== userInfo?.email) {
                modData.readOnly = true
            } else if (requestDetail?.approvers && Object.keys(requestDetail.approvers).length > 0) {
                modData.readOnly = true
            }
        }
    }
    if (canEditCurrentStep) {
        modData.readOnly = data.readOnly === true
        if (conditionalChanges && conditionalChanges.includes("editable")) {
            modData.readOnly = false
        }
    }
    // When viewing a non-current cycle, all fields are disabled regardless of the request/approver context above.
    if (rest.disableAllFields) {
        modData.readOnly = true
    }
    if (hiddenBranch) {
        // Nothing in a hidden branch can be filled in, and a required rule on a field
        // nobody can see would block submission, so the branch is locked and optional.
        modData.readOnly = true
        modData.mandatory = false
    }
    const { readOnly: _parentReadOnly, ...passThroughRest } = rest
    switch (data.formType) {
        case 'section':
            renderElement = <Section spaceData={spaceData} activeElement={null} handleWidgetWidth={() => { }} addWidgetToContainer={() => { }} {...modData} {...passThroughRest} readOnly={undefined} currentStatus={currentStatus} highlightColor={highlightColor} />
            break;

        case 'divider':
            renderElement = <DividerInput {...modData} {...passThroughRest} readOnly={modData.readOnly} />
            break;

        case 'addressField':
            renderElement = <AddressInput {...modData} {...passThroughRest} readOnly={modData.readOnly} setValueData={setValueData} />
            break;

        case 'attachment':
            renderElement = <Attachments {...modData} {...passThroughRest} readOnly={modData.readOnly} currentStatus={currentStatus} />
            break;

        case 'contactField':
            renderElement = <ContactInput {...modData} {...passThroughRest} readOnly={modData.readOnly} setValueData={setValueData} />
            break;

        case 'inputForm':
            renderElement = <TextInput {...modData} {...passThroughRest} readOnly={modData.readOnly} setValueData={setValueData} />
            break;

        case 'textAreaForm':
            renderElement = <TextAreaInput {...modData} {...passThroughRest} readOnly={modData.readOnly} setValueData={setValueData} />
            break;

        case 'numberForm':
            renderElement = (
                <NumberInput
                    key={`${modData?.id || ''}:${modData?.formulaHtml || ''}:${modData?.formula || ''}`}
                    {...modData}
                    {...passThroughRest}
                    readOnly={modData.readOnly}
                    setValueData={setValueData}
                />
            )
            break;

        case 'dateForm':
            renderElement = <DateInput {...modData} {...passThroughRest} readOnly={modData.readOnly} setValueData={setValueData} />
            break;

        case 'infoForm':
            renderElement = <Info {...modData} {...passThroughRest} readOnly={modData.readOnly} />
            break;

        case 'imageForm':
            renderElement = <ImageInput {...modData} {...passThroughRest} readOnly={modData.readOnly} />
            break;

        case 'switchForm':
            renderElement = <SwitchInput {...modData} {...passThroughRest} readOnly={modData.readOnly} setValueData={setValueData} />
            break;

        case 'timeForm':
            renderElement = <TimeInput {...modData} {...passThroughRest} readOnly={modData.readOnly} setValueData={setValueData} />
            break;

        case 'checkboxField':
            renderElement = <CheckboxInput {...modData} {...passThroughRest} readOnly={modData.readOnly} setValueData={setValueData} />
            break;

        case 'dropdownField':
            renderElement = <DropdownInput {...modData} {...passThroughRest} readOnly={modData.readOnly} setValueData={setValueData} />
            break;

        case 'linkField':
            renderElement = <LinkInput {...modData} {...passThroughRest} readOnly={modData.readOnly} setValueData={setValueData} />
            break;

        case 'multipleChoiceField':
            renderElement = <MultipleChoiceInput {...modData} {...passThroughRest} readOnly={modData.readOnly} setValueData={setValueData} />
            break;

        case 'radioField':
            renderElement = <RadioInput {...modData} {...passThroughRest} readOnly={modData.readOnly} setValueData={setValueData} />
            break; 
        case 'userForm':
            renderElement = <UserInput {...modData} {...passThroughRest} readOnly={modData.readOnly} setValueData={setValueData} formType={formType} />
            break;

        case 'tableForm':
            renderElement = <TableInput {...passThroughRest} {...modData} readOnly={modData.readOnly} setValueData={setValueData} lng={lng} />
            break;

        case 'ratingField':
            renderElement = <RatingInput {...modData} {...passThroughRest} readOnly={modData.readOnly} setValueData={setValueData} />
            break;

        case 'ratingScaleField':
            renderElement = <RatingScaleInput {...modData} {...passThroughRest} readOnly={modData.readOnly} setValueData={setValueData} />
            break;

        default:
            renderElement = null;
    }


    // Sections paint themselves (the collapse panel owns the background), so only
    // plain fields are wrapped in the highlight here.
    const highlightStyle = (highlightColor && data.formType !== 'section')
        ? { background: highlightColor, color: getContrastTextColor(highlightColor), padding: "0.5rem", borderRadius: 8 }
        : {}

    if (hiddenBranch) {
        // Mounted so the "set" action reaches the form store, but never on screen.
        // No key here: the parent already supplies the reconciliation key, and keying on
        // setValueData remounted the whole field subtree whenever a condition's value
        // changed - losing the caret, and (for a table, whose Form.Item is
        // preserve={false}) dropping its rows from the store. Widgets apply a
        // conditional value from an effect on activeSetValue, so nothing needs the
        // remount.
        return (
            <div style={{ display: "none" }} aria-hidden="true">
                {renderElement}
            </div>
        )
    }

    return (
        <div style={{ minHeight: data.formType !== 'divider' ? "87px" : "auto", width: data.span ? availableWidth[data.span] : "100%", ...highlightStyle }} className={`hover-wrapper ${data.formType === 'section' ? 'section-drop' : ''} ${highlightStyle.background ? 'condition-highlight' : ''}`}  >
            {renderElement}
        </div>
    )
})
Wrapper.displayName = "Wrapper"

export default Wrapper
